import { useState, useRef, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Stepper,
    Step,
    StepLabel,
    Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { QRCodeSVG } from "qrcode.react";

const steps = ["Escaneá el QR", "Verificá el código"];

const MfaSetupModal = ({ open, onClose, qrUrl, secret, onVerify, loading, step = 1 }) => {
    // step === 2: el usuario ya tiene 2FA y solo verifica un código (login).
    // step === 1: primera configuración (escanear QR + verificar).
    const isLogin = step === 2;
    const [activeStep, setActiveStep] = useState(0);
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [copied, setCopied] = useState(false);
    const [shake, setShake] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (open) {
            setActiveStep(step === 2 ? 1 : 0); // 👈 clave
            setCode(["", "", "", "", "", ""]);
            setCopied(false);
        }
    }, [open, step]);

    const handleCopySecret = () => {
        navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCodeChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newCode = [...code];
        newCode[index] = value.slice(-1);
        setCode(newCode);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
        if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pasted.length === 6) {
            setCode(pasted.split(""));
            inputRefs.current[5]?.focus();
        }
    };

    const handleVerify = async () => {
        const fullCode = code.join("");
        if (fullCode.length < 6) {
            triggerShake();
            return;
        }
        const success = await onVerify(fullCode);
        if (!success) {
            triggerShake();
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        }
    };

    const triggerShake = () => {
        setShake(true);
        setTimeout(() => setShake(false), 500);
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    overflow: "hidden",
                    boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header */}
                <Box
                    sx={{
                        background: "linear-gradient(135deg, #2563EB 0%, #0747a6 100%)",
                        px: 3,
                        pt: 3,
                        pb: 4,
                        position: "relative",
                    }}
                >
                    {!loading && (
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                color: "rgba(255,255,255,0.7)",
                                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                backgroundColor: "rgba(255,255,255,0.15)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ShieldOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>
                                Autenticación en dos pasos
                            </Typography>
                            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                                {isLogin ? "Verificación en dos pasos" : "Configuración requerida"}
                            </Typography>
                        </Box>
                    </Box>

                    <Stepper
                        activeStep={activeStep}
                        sx={{
                            "& .MuiStepLabel-label": {
                                color: "rgba(255,255,255,0.6)",
                                fontSize: "0.72rem",
                                "&.Mui-active": { color: "#fff", fontWeight: 600 },
                                "&.Mui-completed": { color: "rgba(255,255,255,0.8)" },
                            },
                            "& .MuiStepIcon-root": {
                                color: "rgba(255,255,255,0.25)",
                                "&.Mui-active": { color: "#fff" },
                                "&.Mui-completed": { color: "rgba(255,255,255,0.7)" },
                            },
                            "& .MuiStepIcon-text": { fill: "#2563EB", fontSize: "0.65rem", fontWeight: 700 },
                            "& .MuiStepConnector-line": { borderColor: "rgba(255,255,255,0.2)" },
                        }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Body */}
                <Box sx={{ px: 3, py: 3 }}>
                    {/* Step 1 - QR */}
                    <Collapse in={activeStep === 0} unmountOnExit>
                        <Box sx={{ textAlign: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, justifyContent: "center" }}>
                                <QrCode2Icon sx={{ color: "#2563EB", fontSize: 18 }} />
                                <Typography sx={{ fontSize: "0.82rem", color: "#444", fontWeight: 500 }}>
                                    Escaneá con Google Authenticator o Authy
                                </Typography>
                            </Box>

                            {/* QR Code */}
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    p: 2,
                                    borderRadius: "16px",
                                    border: "2px solid #e8f0fe",
                                    backgroundColor: "#fff",
                                    mb: 2.5,
                                    boxShadow: "0 4px 16px rgba(0,82,204,0.08)",
                                }}
                            >
                                {qrUrl ? (
                                    <QRCodeSVG value={qrUrl} size={160} level="M" />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 160,
                                            height: 160,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>Sin QR</Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Manual secret */}
                            <Typography sx={{ fontSize: "0.72rem", color: "#999", mb: 1 }}>
                                ¿No podés escanear? Ingresá la clave manualmente:
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    backgroundColor: "#f4f6fb",
                                    borderRadius: "10px",
                                    px: 1.5,
                                    py: 0.75,
                                    mb: 3,
                                    border: "1px solid #e0e7f0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        flex: 1,
                                        fontFamily: "'Courier New', monospace",
                                        fontSize: "0.78rem",
                                        color: "#333",
                                        letterSpacing: "0.08em",
                                        wordBreak: "break-all",
                                        textAlign: "left",
                                    }}
                                >
                                    {secret}
                                </Typography>
                                <Tooltip title={copied ? "¡Copiado!" : "Copiar"} placement="top">
                                    <IconButton size="small" onClick={handleCopySecret} sx={{ color: copied ? "#2e7d32" : "#2563EB" }}>
                                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                disableElevation
                                onClick={() => setActiveStep(1)}
                                sx={{
                                    borderRadius: "12px",
                                    py: 1.2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.9rem",
                                    backgroundColor: "#2563EB",
                                    "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(0,82,204,0.28)" },
                                }}
                            >
                                Ya lo escaneé →
                            </Button>
                        </Box>
                    </Collapse>

                    {/* Step 2 - Code verification */}
                    <Collapse in={activeStep === 1} unmountOnExit>
                        <Box sx={{ textAlign: "center" }}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: "14px",
                                    backgroundColor: "#e8f0fe",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mx: "auto",
                                    mb: 2,
                                }}
                            >
                                <LockOpenOutlinedIcon sx={{ color: "#2563EB", fontSize: 26 }} />
                            </Box>

                            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a", mb: 0.5 }}>
                                Ingresá el código
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem", color: "#888", mb: 3 }}>
                                Abrí tu app de autenticación y escribí
                                <br />el código de 6 dígitos que aparece
                            </Typography>

                            {/* 6-digit input */}
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "center",
                                    mb: 3,
                                    animation: shake ? "shake 0.4s ease" : "none",
                                    "@keyframes shake": {
                                        "0%,100%": { transform: "translateX(0)" },
                                        "20%": { transform: "translateX(-6px)" },
                                        "40%": { transform: "translateX(6px)" },
                                        "60%": { transform: "translateX(-4px)" },
                                        "80%": { transform: "translateX(4px)" },
                                    },
                                }}
                                onPaste={handlePaste}
                            >
                                {code.map((digit, index) => (
                                    <TextField
                                        key={index}
                                        inputRef={(el) => (inputRefs.current[index] = el)}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        inputProps={{ maxLength: 1, style: { textAlign: "center", fontWeight: 700, fontSize: "1.2rem", padding: "10px 0" } }}
                                        sx={{
                                            width: 44,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "10px",
                                                backgroundColor: digit ? "#e8f0fe" : "#f4f6fb",
                                                transition: "all 0.15s ease",
                                                "& fieldset": {
                                                    borderColor: digit ? "#2563EB" : "#e0e0e0",
                                                    borderWidth: digit ? "2px" : "1px",
                                                },
                                                "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "2px" },
                                                "&.Mui-focused": { backgroundColor: "#fff" },
                                            },
                                        }}
                                    />
                                ))}
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                disableElevation
                                onClick={handleVerify}
                                disabled={loading || code.join("").length < 6}
                                sx={{
                                    borderRadius: "12px",
                                    py: 1.2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.9rem",
                                    backgroundColor: "#2563EB",
                                    mb: 1.5,
                                    "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(0,82,204,0.28)" },
                                    "&.Mui-disabled": { backgroundColor: "#b3c9e8" },
                                }}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : "Verificar y continuar"}
                            </Button>

                            {!isLogin && (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => { setActiveStep(0); setCode(["", "", "", "", "", ""]); }}
                                    disabled={loading}
                                    sx={{ textTransform: "none", color: "#888", fontSize: "0.78rem" }}
                                >
                                    ← Volver al QR
                                </Button>
                            )}
                        </Box>
                    </Collapse>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MfaSetupModal;